import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SchedulerDialogComponent } from '@app/modules/integrated-workflows/scheduler-dialog/scheduler-dialog.component';
import { Labels } from '@app/util/labels.util';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@app/services/toast.service';
import { CommonFunctionalitiesService } from '@app/services/common-functionalities.service';;

@Component({
  selector: 'app-scheduler',
  templateUrl: './scheduler.component.html',
  styleUrls: ['./scheduler.component.scss']
})

export class SchedulerComponent implements OnInit {

  /****************** Variable declaration ******************/
  /**
   * Labels used in the component
   */
  scheduleNm: string = Labels.scheduleNm;
  scheduleTime: string = Labels.scheduleTime;
  repeat: string = Labels.repeat;
  startDt: string = Labels.startDate;
  endDt: string = Labels.endDate;

  name: any;
  scheduleForm: FormGroup;
  timeOptions: string[];
  skdValueOptions: string[] = ['Daily', 'Weekly', 'Monthly', 'Yearly', 'Custom']; // Options for skdValue
  startDate: any;
  endDate: any;
  repeatDay: any;
  repeatValue: any;
  today: Date;
  skdValueMapping: { [key: string]: number };
  repeatValueMapping: { [key: string]: number };
  editSdkCustomValue: boolean = false;

  /****************** Input and Output decorators ******************/
  @Output() schedulerData: EventEmitter<any> = new EventEmitter<any>();
  @Input() editSchedulerData: any;
  @Input() mode: any;
  @Output() statusChange = new EventEmitter<boolean>();

  /**
   * Constructor
   * @param dialog 
   * @param formBuilder 
   * @param ts 
   * @param cf 
   */
  constructor(public dialog: MatDialog,
    private formBuilder: FormBuilder,
    public ts: ToastService,
    public cf: CommonFunctionalitiesService) {
    this.today = new Date();
  }

  /**ngOnInit */
  ngOnInit() {
    this.formValidation();
    this.setListeners();
    this.timeOptions = this.cf.generateTimeOptions();
    this.startDate = new Date(); // Set the start date to the current day

    if (this.editSchedulerData) {
      this.editScheduler();
    } else {
      this.scheduleForm.reset;
    }

    if (this.mode === 'read') {
      this.scheduleForm.disable();
    } else {
      this.scheduleForm.enable();
    }
  }

  /**setListeners */
  setListeners() {
    this.scheduleForm.statusChanges.subscribe(() => this.statusChange.emit(this.scheduleForm.valid));
  }

  /**formValidation */
  formValidation() {
    /** Initialize the form group and form controls */
    this.scheduleForm = this.formBuilder.group(
      {
        skdName: ['', Validators.required],
        skdTime: ['', Validators.required],
        skdValue: ['', Validators.required],
        startDate: ['', Validators.required],
        endDate: ['', Validators.required],
      });
    this.statusChange.emit(this.scheduleForm.valid);
  }

  /**
   * function to validate the date input
   * @param event 
   * @param field form control name
   */
  dateValidation(event: any, field: string) {
    if (!/^[^a-zA-Z]*$/.test(event.target.value)) {
      this.scheduleForm.patchValue({
        [field]: ''
      })
    }
  }

  /**
   * OnSkdValueChange function is used if the user selects the custom option in dropdown
  */
  onSkdValueChange(): void {
    const skdValueControl = this.scheduleForm.get('skdValue');
    if (!this.editSdkCustomValue) {
      if (skdValueControl && skdValueControl.value === 'Custom') {
        this.schedulerDialog();
      }
    }
  }

  /**
   * schedulerDialog is a function which invokes the schedulerDialog as a dialog box
   */
  schedulerDialog(): void {
    const dialogRef = this.dialog.open(SchedulerDialogComponent, {
      height: "300px",
      width: "600px",
      data: { name: this.name }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.repeatDay = result.repeatDay;
        this.repeatValue = result.repeatValue;
      }
    });
  }

  /**
   * getSchedulerData is used to pass the data to the parent component
   * @returns time
   */
  getSchedulerData() {
    const formValue = this.scheduleForm.value;
    const skdValue = formValue.skdValue;

    // Mapping object for skdValue options and their corresponding values
    this.skdValueMapping = { Daily: 1, Weekly: 7, Monthly: 30, Yearly: 365 };
    this.repeatValueMapping = { Day: 1, Week: 7, Month: 30, Year: 365 };

    let timeInMillis;
    if (skdValue === 'Custom') {
      timeInMillis = this.cf.getMilisecFromDuration(this.repeatValueMapping[this.repeatValue], this.repeatDay)
    } else {
      timeInMillis = this.cf.getMilisecFromTime(this.skdValueMapping[skdValue]);
    }
    formValue.scheduleTimeInMills = timeInMillis;
    return formValue;
  }

  /**
   * editscheduler is used to patch the values while user is in edit mode
   */
  editScheduler() {
    const startDate = new Date(this.editSchedulerData.startTime);
    const endDate = new Date(this.editSchedulerData.endTime);
    const parsedTime = new Date(this.editSchedulerData.startTime);
    const formattedTime = parsedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const parsedPeriod = this.parsePeriod(this.editSchedulerData.period);
    const { value, unit } = parsedPeriod;

    const unitMapping: { [key: string]: string } = {
      'day': 'Daily',
      'week': 'Weekly',
      'month': 'Monthly',
      'year': 'Yearly',
      'custom': 'Custom',
    };

    if (unitMapping[unit] === 'Custom') {
      this.editSdkCustomValue = true;
    }

    this.scheduleForm.patchValue({
      skdName: this.editSchedulerData.name || '',
      skdTime: formattedTime || '',
      startDate: startDate || '',
      endDate: endDate || '',
      skdValue: unitMapping[unit] || '',
    })
  }

  /**parsePeriod */
  parsePeriod(period: string): { value: number, unit: string } {
    const matches = period.match(/(\d+)\s*(day|week|weeks|month|year)/i);

    if (matches && matches.length === 3) {
      const value = parseInt(matches[1], 10);
      const unit = matches[2].toLowerCase();

      if (value > 1) {
        return { value: 0, unit: 'custom' };
      }
      return { value, unit };
    }
    return { value: 0, unit: 'custom' };
  }
}
